<script setup>
// A tela da Gestão, como no protótipo: uma tela só, com Monitoramento e
// Auditoria nas abas do topo. O topo também tem o período, os filtros e o
// botão do modo TV; a Auditoria tem os números da equipe sempre visíveis e as
// abas dela (relatório da IA, avaliações, retenção e vendas, custos,
// configuração) logo abaixo.
//
// Visual próprio, o azul escuro da Nexus Neural, independente do tema do
// painel: a classe `dark` liga as cores escuras do Chatwoot e o
// `.neuxs-gestao` troca a base delas pelo azul (ver o <style> no fim).
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { useAccount } from 'dashboard/composables/useAccount';
import { useRecursos } from '../nexus/recursos';
import MonitoramentoPainel from './MonitoramentoPainel.vue';
import AuditoriaRelatorio from './AuditoriaRelatorio.vue';
import AuditoriaAvaliacoes from './AuditoriaAvaliacoes.vue';
import AuditoriaRetencao from './AuditoriaRetencao.vue';
import AuditoriaCustos from './AuditoriaCustos.vue';
import AuditoriaConfiguracao from './AuditoriaConfiguracao.vue';
import { createGestaoApi, nota } from './api';
import messages from './i18n';

const props = defineProps({
  visao: { type: String, default: 'MONITORAMENTO' },
  aba: { type: String, default: 'relatorio' },
});

const { t } = useI18n({ messages });
const router = useRouter();
const { accountId } = useAccount();
const recursos = useRecursos();
const api = computed(() => createGestaoApi(accountId.value));

// As abas da Auditoria; `periodo`: a aba usa o período do topo.
const ABAS = [
  { id: 'relatorio', texto: 'RELATORIO', componente: AuditoriaRelatorio },
  { id: 'avaliacoes', texto: 'AVALIACOES', componente: AuditoriaAvaliacoes, periodo: true },
  { id: 'retencao', texto: 'RETENCAO', componente: AuditoriaRetencao, periodo: true },
  { id: 'custos', texto: 'CUSTOS', componente: AuditoriaCustos, foraDoPlano: true },
  { id: 'configuracao', texto: 'CONFIGURACAO', componente: AuditoriaConfiguracao },
];
const PERIODOS_DO_MONITOR = [0, 7, 30];
const PERIODOS_DA_AUDITORIA = [7, 30, 90];

const podeMonitorar = computed(() => Boolean(recursos.value?.administrador));
const podeAuditar = computed(() => Boolean(recursos.value?.auditoria));
const liberada = computed(() => (props.visao === 'AUDITORIA' ? podeAuditar.value : podeMonitorar.value));
// Plano sem a Auditoria: só a aba Custos (o consumo do plano).
const noPlano = computed(() => recursos.value?.auditoria_no_plano !== false);
const abas = computed(() => (noPlano.value ? ABAS : ABAS.filter(aba => aba.foraDoPlano)));
const abaAtual = computed(() => abas.value.find(aba => aba.id === props.aba) || abas.value[0]);

// Monitoramento
const diasDoMonitor = ref(0);
const caixa = ref('');
const time = ref('');
const filtros = ref({ caixas: [], times: [] });

// Auditoria: o período e os números da equipe, iguais em todas as abas.
const diasDaAuditoria = ref(7);
const resumo = ref(null);
const auditoriaLigada = ref(true);
const carregaResumo = async () => {
  if (props.visao !== 'AUDITORIA' || !podeAuditar.value || !noPlano.value) return;
  try {
    const { data } = await api.value.avaliacoes({ dias: diasDaAuditoria.value });
    resumo.value = data.resumo;
    auditoriaLigada.value = data.ativa;
  } catch {
    resumo.value = null;
  }
};
watch([() => props.visao, diasDaAuditoria, podeAuditar, noPlano], carregaResumo, { immediate: true });

const evolucao = computed(() => {
  const { nota_media: atual, nota_anterior: anterior } = resumo.value || {};
  if (atual === null || atual === undefined || anterior === null || anterior === undefined) {
    return { texto: t('NEUXS_GESTAO.RESUMO.SEM_ANTERIOR'), cor: 'text-n-slate-11' };
  }
  const diferenca = Math.round((atual - anterior) * 10) / 10;
  return {
    texto: t('NEUXS_GESTAO.RESUMO.VS', { sinal: diferenca >= 0 ? '▲' : '▼', n: nota(Math.abs(diferenca)) }),
    cor: diferenca >= 0 ? 'text-n-teal-11' : 'text-n-ruby-11',
  };
});

const vai = (visao, aba) => {
  const rota =
    visao === 'AUDITORIA'
      ? { name: 'neuxs_gestao_auditoria', params: { accountId: accountId.value, aba: aba || 'relatorio' } }
      : { name: 'neuxs_gestao_monitoramento', params: { accountId: accountId.value } };
  router.push(rota);
};
// No celular, os filtros do topo ficam atrás de um botão.
const filtrosAbertos = ref(false);
const temFiltros = computed(
  () =>
    (props.visao === 'MONITORAMENTO' && podeMonitorar.value) ||
    (props.visao === 'AUDITORIA' && podeAuditar.value && abaAtual.value.periodo)
);

const abreTv = () => router.push({ name: 'neuxs_gestao_tv', params: { accountId: accountId.value } });
</script>

<template>
  <section
    class="dark neuxs-gestao flex flex-col w-full h-full overflow-y-auto md:overflow-hidden bg-n-surface-1 text-n-slate-12"
  >
    <header class="px-4 border-b md:px-6 border-n-weak">
      <div class="flex flex-wrap items-center w-full max-w-6xl gap-x-5 gap-y-3 py-3 mx-auto md:py-4">
        <nav class="flex items-center gap-1 me-auto">
          <button
            v-if="podeMonitorar"
            type="button"
            class="px-2.5 pb-1.5 pt-1 text-lg font-semibold border-b-2"
            :class="visao === 'MONITORAMENTO' ? 'border-n-blue-9 text-n-slate-12' : 'border-transparent text-n-slate-11 hover:text-n-slate-12'"
            @click="vai('MONITORAMENTO')"
          >
            {{ t('NEUXS_GESTAO.MENU.MONITORAMENTO') }}
          </button>
          <button
            v-if="podeAuditar"
            type="button"
            class="flex items-center gap-2 px-2.5 pb-1.5 pt-1 text-lg font-semibold border-b-2"
            :class="visao === 'AUDITORIA' ? 'border-n-blue-9 text-n-slate-12' : 'border-transparent text-n-slate-11 hover:text-n-slate-12'"
            @click="vai('AUDITORIA', aba)"
          >
            {{ t('NEUXS_GESTAO.MENU.AUDITORIA') }}
            <span class="px-2 py-0.5 text-[11px] font-normal whitespace-nowrap border border-dashed rounded-full border-n-weak text-n-slate-11">
              {{ t('NEUXS_GESTAO.POR_PESSOA') }}
            </span>
          </button>
        </nav>

        <Button
          v-if="temFiltros"
          class="md:!hidden"
          size="sm"
          variant="faded"
          color="slate"
          icon="i-lucide-sliders-horizontal"
          :label="t('NEUXS_GESTAO.FILTROS_BOTAO')"
          @click="filtrosAbertos = !filtrosAbertos"
        />

        <div
          v-if="visao === 'MONITORAMENTO' && podeMonitorar"
          class="flex-wrap items-center w-full gap-3 md:flex md:w-auto"
          :class="filtrosAbertos ? 'flex' : 'hidden'"
        >
          <div class="flex overflow-hidden border rounded-lg border-n-weak">
            <button
              v-for="n in PERIODOS_DO_MONITOR"
              :key="n"
              type="button"
              class="px-3 py-1.5 text-sm"
              :class="diasDoMonitor === n ? 'bg-n-blue-3 text-n-blue-11' : 'text-n-slate-11 hover:bg-n-alpha-1'"
              @click="diasDoMonitor = n"
            >
              {{ n === 0 ? t('NEUXS_GESTAO.MONITOR.HOJE') : t('NEUXS_GESTAO.MONITOR.DIAS', { n }) }}
            </button>
          </div>
          <select v-model="caixa" class="!mb-0 !w-auto">
            <option value="">{{ t('NEUXS_GESTAO.MONITOR.CAIXAS') }}</option>
            <option v-for="c in filtros.caixas" :key="c.id" :value="String(c.id)">{{ c.nome }}</option>
          </select>
          <select v-model="time" class="!mb-0 !w-auto">
            <option value="">{{ t('NEUXS_GESTAO.MONITOR.TIMES') }}</option>
            <option v-for="c in filtros.times" :key="c.id" :value="String(c.id)">{{ c.nome }}</option>
          </select>
          <Button icon="i-lucide-tv" :label="t('NEUXS_GESTAO.MONITOR.MODO_TV')" @click="abreTv" />
        </div>

        <div
          v-else-if="visao === 'AUDITORIA' && podeAuditar && abaAtual.periodo"
          class="overflow-hidden border rounded-lg md:flex border-n-weak"
          :class="filtrosAbertos ? 'flex' : 'hidden'"
        >
          <button
            v-for="n in PERIODOS_DA_AUDITORIA"
            :key="n"
            type="button"
            class="px-3 py-1.5 text-sm"
            :class="diasDaAuditoria === n ? 'bg-n-blue-3 text-n-blue-11' : 'text-n-slate-11 hover:bg-n-alpha-1'"
            @click="diasDaAuditoria = n"
          >
            {{ t('NEUXS_GESTAO.MONITOR.DIAS', { n }) }}
          </button>
        </div>
      </div>
    </header>

    <main class="flex-1 px-4 md:px-6 md:overflow-y-auto">
      <div class="w-full max-w-6xl py-5 mx-auto">
        <div v-if="recursos === null" class="flex justify-center py-10"><Spinner /></div>
        <p v-else-if="!liberada" class="p-4 text-sm rounded-xl bg-n-alpha-2 text-n-slate-11">
          {{ t('NEUXS_GESTAO.SEM_ACESSO') }}
        </p>

        <MonitoramentoPainel
          v-else-if="visao === 'MONITORAMENTO'"
          :key="`monitor-${accountId}`"
          :account-id="accountId"
          :dias="diasDoMonitor"
          :caixa="caixa"
          :time="time"
          @filtros="valor => (filtros = valor)"
        />

        <div v-else class="flex flex-col gap-5">
          <p v-if="noPlano && !auditoriaLigada" class="p-3 m-0 text-sm rounded-xl bg-n-amber-3 text-n-amber-11">
            {{ t('NEUXS_GESTAO.DESLIGADA') }}
          </p>
          <div v-if="noPlano" class="grid grid-cols-2 gap-3 md:grid-cols-4">
            <div class="px-4 py-3 border rounded-xl border-n-weak bg-n-alpha-1">
              <span class="text-xs tracking-wide uppercase text-n-slate-11">{{ t('NEUXS_GESTAO.RESUMO.NOTA') }}</span>
              <strong class="block text-3xl font-normal tabular-nums">{{ nota(resumo?.nota_media) }}</strong>
              <span class="text-xs" :class="evolucao.cor">{{ evolucao.texto }}</span>
            </div>
            <div class="px-4 py-3 border rounded-xl border-n-weak bg-n-alpha-1">
              <span class="text-xs tracking-wide uppercase text-n-slate-11">{{ t('NEUXS_GESTAO.RESUMO.AVALIADAS') }}</span>
              <strong class="block text-3xl font-normal tabular-nums">{{ resumo?.avaliadas ?? '—' }}</strong>
              <span class="text-xs text-n-slate-11">{{ t('NEUXS_GESTAO.DIAS', { n: diasDaAuditoria }) }}</span>
            </div>
            <div class="px-4 py-3 border rounded-xl bg-n-alpha-1" :class="resumo?.com_alerta ? 'border-n-amber-9' : 'border-n-weak'">
              <span class="text-xs tracking-wide uppercase text-n-slate-11">{{ t('NEUXS_GESTAO.RESUMO.ALERTAS') }}</span>
              <strong class="block text-3xl font-normal tabular-nums" :class="resumo?.com_alerta ? 'text-n-ruby-11' : ''">
                {{ resumo?.com_alerta ?? '—' }}
              </strong>
            </div>
            <div class="px-4 py-3 border rounded-xl border-n-weak bg-n-alpha-1">
              <span class="text-xs tracking-wide uppercase text-n-slate-11">{{ t('NEUXS_GESTAO.RESUMO.ABAIXO') }}</span>
              <strong class="block text-3xl font-normal tabular-nums" :class="resumo?.abaixo_de_6 ? 'text-n-amber-11' : ''">
                {{ resumo?.abaixo_de_6 ?? '—' }}
              </strong>
            </div>
          </div>

          <nav class="flex flex-wrap gap-2">
            <button
              v-for="item in abas"
              :key="item.id"
              type="button"
              class="px-3.5 py-1.5 text-sm border rounded-full"
              :class="abaAtual.id === item.id ? 'border-n-blue-9 bg-n-blue-3 text-n-blue-11' : 'border-n-weak bg-n-alpha-1 text-n-slate-11 hover:text-n-slate-12'"
              @click="vai('AUDITORIA', item.id)"
            >
              {{ t(`NEUXS_GESTAO.MENU.${item.texto}`) }}
            </button>
          </nav>

          <component
            :is="abaAtual.componente"
            :key="`${abaAtual.id}-${accountId}`"
            :account-id="accountId"
            :dias="diasDaAuditoria"
            @mudou="carregaResumo"
          />
        </div>
      </div>
    </main>
  </section>
</template>

<style>
/* A paleta do protótipo (azul da Nexus Neural) por cima das cores escuras do
   Chatwoot, só dentro desta tela: os componentes usam as mesmas classes n-*. */
.neuxs-gestao.dark {
  --background-color: 7 13 26;
  --surface-1: 7 13 26;
  --surface-2: 11 21 39;
  --solid-1: 11 21 39;
  --border-weak: 26 43 71;
  --border-strong: 44 66 104;
  --alpha-1: 15, 28, 51, 0.85;
  --alpha-2: 90, 169, 255, 0.1;
  --alpha-3: 15, 28, 51, 0.96;
  --slate-1: 7 13 26;
  --slate-2: 11 21 39;
  --slate-3: 15 28 51;
  --slate-4: 20 36 64;
  --slate-5: 26 43 71;
  --slate-6: 33 52 84;
  --slate-7: 44 66 104;
  --slate-8: 64 86 124;
  --slate-9: 77 96 128;
  --slate-10: 98 117 148;
  --slate-11: 125 145 176;
  --slate-12: 219 230 245;
  --blue-3: 18 40 74;
  --blue-6: 40 80 140;
  --blue-9: 90 169 255;
  --blue-11: 156 200 255;
}
</style>
